import React from 'react';
import { Modal, Table } from 'react-bootstrap';
import { SalesEntryData, SalesEntryItem } from 'contexts/SalesEntryContext';
import { Summary } from 'devextreme-react/cjs/data-grid';

interface CalculationModalProps {
  show: boolean;
  onHide: () => void;
  salesEntryData: SalesEntryData;
}

const CalculationModal: React.FC<CalculationModalProps> = ({
  show,
  onHide,
  salesEntryData,
}) => {
  // Calculate detailed values for each item based on actual data structure
  const calculateItemDetails = (item: SalesEntryItem) => {
    // Use actual properties from SalesEntryItem interface
    const goldRate = item.goldMrp || 0;
    const netWt = item.netWt || 0;
    const karatType = item.karatType || item.kt || '22K';

    // Calculate karat purity percentage
    const karatPurity = karatType === '24K' ? 1 : karatType === '22K' ? 0.916 : karatType === '18K' ? 0.750 : 0.916;

    // Stone/Diamond calculations - calculated from diamond weight and rate
    const stoneWt = item.diaGoldWt || 0;
    const stoneMrp = item.diaMrp || 0;
    const stonePrice = stoneWt * stoneMrp;

    // Metal amount calculation - based on net weight, gold rate and purity
    const metalAmt = netWt * goldRate * karatPurity;

    // Diamond amount calculation - use diamond MRP
    const diaAmt = item.diaMrp || 0;

    // Labour amount calculation - use labour MRP
    const labAmt = item.makingCharges || 0;

    // Sub selling price calculation
    const subSellingPrice = item.mrpPrice;

    // Calculate discounts - these would need to be added to the interface if available
    const labDis = Number(item.labDiscount) || 0;
    const diaDis = Number(item.diamondDiscount) || 0;
    const metalDis = Number(item.materialDiscount) || 0;
    const rewardDis = Number(item.rewardDis) || 0;
    // const totalPriceDis = labDis + diaDis + metalDis + item.mrpDiscount;
    const totalPriceDis = item.mrpDiscount;
    // const totalPriceDiscount = item.mrpDiscount + item.labDiscount + item.

    // Store offer and gift voucher - these would need to be added to interface if available
    const storeOffer = (salesEntryData.summary.storeOffer) || 0; // Not in current interface
    const giftVou = 0; // Not in current interface

    // Final selling price calculation
    // const sellingPrice = subSellingPrice - (totalPriceDis  - giftVou);
    const sellingPrice = subSellingPrice - (totalPriceDis + labDis + diaDis + metalDis);

    // Calculate complexity percentage - not in current interface
    const complexityPercentage = 0;

    const gstAmt = sellingPrice * 0.03;
    const withGstAmt = sellingPrice + gstAmt;
    const lastPrice = withGstAmt - (item.totalRewardDiscount || 0);
    return {
      stonePrice,
      metalAmt,
      diaAmt,
      labAmt,
      subSellingPrice,
      labDis,
      diaDis,
      metalDis,
      totalPriceDis,
      storeOffer,
      giftVou,
      sellingPrice,
      goldRate,
      complexityPercentage,

      gstAmt,
      withGstAmt,
      lastPrice,
      rewardDis,
    };
  };

  // Calculate totals for all items
  const calculateTotals = () => {
    return salesEntryData.items.reduce(
      (totals, item) => {
        const itemCalc = calculateItemDetails(item);
        const gstAmt = (itemCalc.sellingPrice || 0) * 0.03;
        const withGstAmt = (itemCalc.sellingPrice || 0) + gstAmt;
        const lastPrice = withGstAmt - (item.totalRewardDiscount || 0);

        return {
          stonePrice: totals.stonePrice + (item.colorStonePrice || 0),
          metalAmt: totals.metalAmt + (item.metalAmount || 0),
          diaAmt: totals.diaAmt + (item.diaMrp || 0),
          labAmt: totals.labAmt + (item.makingCharges || 0),
          subSellingPrice: totals.subSellingPrice + (item.mrpPrice || 0),
          labDis: totals.labDis + itemCalc.labDis,
          diaDis: totals.diaDis + itemCalc.diaDis,
          metalDis: totals.metalDis + itemCalc.metalDis,
          totalPriceDis: totals.totalPriceDis + itemCalc.totalPriceDis,
          storeOffer: totals.storeOffer + itemCalc.storeOffer,
          giftVou: totals.giftVou + itemCalc.giftVou,
          sellingPrice: totals.sellingPrice + itemCalc.sellingPrice,

          // new fields
          gstAmt: totals.gstAmt + gstAmt,
          rewardDis: totals.rewardDis + itemCalc.rewardDis,
          lastPrice: totals.lastPrice + lastPrice,

        };
      },
      {
        stonePrice: 0,
        metalAmt: 0,
        diaAmt: 0,
        labAmt: 0,
        subSellingPrice: 0,
        labDis: 0,
        diaDis: 0,
        metalDis: 0,
        totalPriceDis: 0,
        storeOffer: 0,
        giftVou: 0,
        sellingPrice: 0,
        // new initial values
        gstAmt: 0,
        rewardDis: 0,
        lastPrice: 0,

      }
    );
  };

  const totals = calculateTotals();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatTotalPriceDiscountByProduct = (productId: string) => {
    let totalPriceDiscount = 0;

    salesEntryData
      .consumerOfferDetail?.filter(item => item.isSelected && item.productId == productId)
      .forEach(item => totalPriceDiscount += item.discountValue);

    return formatCurrency(totalPriceDiscount);
  };

  const formatWeight = (weight: number) => `${weight.toFixed(3)}`;
  const formatPercentage = (percentage: number) => `${percentage.toFixed(1)}%`;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Product Calculation</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        {/* Main Calculation Table - With actual data */}
        <div className="mb-4">
          <Table bordered hover responsive size="sm" className="calculation-table">
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: '80px' }}>Tag</th>
                <th style={{ minWidth: '100px' }}>IGI No.</th>
                <th style={{ minWidth: '100px' }}>HUID</th>
                <th style={{ minWidth: '100px' }}>Material type</th>
                <th style={{ minWidth: '120px' }}>Complexity name</th>
                <th style={{ minWidth: '100px' }}>Complexity %</th>
                <th style={{ minWidth: '100px' }}>Today gold rate</th>
                <th style={{ minWidth: '60px' }}>Metal Kt</th>
                <th style={{ minWidth: '80px' }}>Net Wt</th>
                <th style={{ minWidth: '80px' }}>Color</th>
                <th style={{ minWidth: '120px' }}>Stone Price</th>
                <th style={{ minWidth: '120px' }}>Metal Amt</th>
                <th style={{ minWidth: '120px' }}>Dia Amt</th>
                <th style={{ minWidth: '120px' }}>Lab Amt</th>
                <th style={{ minWidth: '140px' }}>Selling Price Type</th>
                <th style={{ minWidth: '140px' }}>Sub Selling Price</th>
                <th style={{ minWidth: '80px' }}>Lab Dis</th>
                <th style={{ minWidth: '80px' }}>Dia Dis</th>
                <th style={{ minWidth: '90px' }}>Metal Dis</th>
                <th style={{ minWidth: '120px' }}>Total Price Dis</th>
                {/*  <th style={{ minWidth: '100px' }}>Store Offer</th>*/}
                {/* <th style={{ minWidth: '80px' }}>Gift Vou</th> */}
                <th style={{ minWidth: '120px' }}>Selling Price</th>
                <th style={{ minWidth: '120px' }}>3% GST</th>
                <th style={{ minWidth: '120px' }}>Reward Dis</th>
                <th style={{ minWidth: '120px' }}>Total Price</th>
              </tr>
            </thead>
            <tbody>
              {salesEntryData.items.map((item) => {
                const calc = calculateItemDetails(item);
                return (
                  <tr key={item.id}>
                    <td>{item.tag || ''}</td>
                    <td>{item.igiNo || ''}</td>
                    <td>{item.huidNo || ''}</td>
                    <td>{item.materialTypeName}</td>
                    <td>{item.complexityName}</td>
                    {/* <td>{calc.complexityPercentage > 0 ? formatPercentage(calc.complexityPercentage) : ''}</td> */}
                    <td>{item.complexityPercentage}</td>
                    <td>{item.todaysGoldRate}</td>
                    <td>{item.karatType || item.kt || ''}</td>
                    <td>{formatWeight(item.netWt || 0)}</td>
                    <td>{item.color || ''}</td>
                    {/* <td className="text-end">{formatCurrency(calc.stonePrice)}</td> */}
                    <td className="text-end">{formatCurrency(item.colorStonePrice)}</td>
                    {/* <td className="text-end">{formatCurrency(calc.metalAmt)}</td> */}
                    <td className="text-end">{formatCurrency(item.metalAmount)}</td>
                    <td className="text-end">{formatCurrency(calc.diaAmt)}</td>
                    {/* <td className="text-end">{formatCurrency(calc.labAmt)}</td> */}
                    <td className="text-end">{formatCurrency(item.makingCharges)}</td>
                    <td className='text-end'>{item.isSRP ? 'SRP' : 'MRP'}</td>
                    {/* <td className="text-end">{formatCurrency(calc.subSellingPrice)}</td> */}
                    <td className="text-end">{formatCurrency(item.mrpPrice)}</td>
                    <td className="text-end">{formatCurrency(calc.labDis)}</td>
                    <td className="text-end">{formatCurrency(calc.diaDis)}</td>
                    <td className="text-end">{formatCurrency(calc.metalDis)}</td>
                    {/* <td className="text-end">{formatTotalPriceDiscountByProduct(item.id)}</td> */}
                    <td className="text-end">{formatCurrency(calc.totalPriceDis)}</td>
                    {/* <td className="text-end">{formatCurrency(salesEntryData.summary.consumerOffer)}</td> */}
                    {/* <td className="text-end">{formatCurrency(calc.storeOffer)}</td> */}
                    {/* <td className="text-end">{formatCurrency(calc.giftVou)}</td> */}
                    <td className="text-end fw-bold">{formatCurrency(calc.sellingPrice)}</td>
                    <td className="text-end fw-bold">{formatCurrency(calc.gstAmt)}</td>
                    <td className="text-end fw-bold">{formatCurrency(calc.rewardDis)}</td>
                    <td className="text-end fw-bold">{formatCurrency(calc.lastPrice)}</td>
                  </tr>
                );
              })}
              <tr className="table-warning fw-bold">
                <td><strong>Total</strong></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td><strong>{formatWeight(salesEntryData.items.reduce((sum, item) => sum + (item.netWt || 0), 0))}</strong></td>
                <td></td>
                <td className="text-end"><strong>{formatCurrency(totals.stonePrice)}</strong></td>
                <td className="text-end"><strong>{formatCurrency(totals.metalAmt)}</strong></td>
                <td className="text-end"><strong>{formatCurrency(totals.diaAmt)}</strong></td>
                <td className="text-end"><strong>{formatCurrency(totals.labAmt)}</strong></td>
                <td></td>
                <td className="text-end"><strong>{formatCurrency(totals.subSellingPrice)}</strong></td>
                <td className="text-end"><strong>{formatCurrency(totals.labDis)}</strong></td>
                <td className="text-end"><strong>{formatCurrency(totals.diaDis)}</strong></td>
                <td className="text-end"><strong>{formatCurrency(totals.metalDis)}</strong></td>
                <td className="text-end"><strong>{formatCurrency(totals.totalPriceDis)}</strong></td>
                {/* <td className="text-end"><strong>{formatCurrency(totals.storeOffer)}</strong></td> */}
                {/* <td className="text-end"><strong>{formatCurrency(totals.giftVou)}</strong></td> */}
                <td className="text-end"><strong>{formatCurrency(totals.sellingPrice)}</strong></td>
                <td className="text-end"><strong>{formatCurrency(totals.gstAmt)}</strong></td>
                <td className="text-end"><strong>{formatCurrency(totals.rewardDis)}</strong></td>
                <td className="text-end"><strong>{formatCurrency(totals.lastPrice)}</strong></td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* Summary Section - Use actual state summary */}
        <div className="mb-4">
          <h6 className="mb-3">Calculation Summary</h6>
          <Table bordered size="sm" className="summary-table">
            <tbody>
              <tr>
                <td className="fw-bold">Ornament Amount</td>
                <td className="text-end">{formatCurrency(salesEntryData.summary.ornamentAmt)}</td>
              </tr>
              <tr>
                <td className="fw-bold">Sub Amount</td>
                <td className="text-end">{formatCurrency(salesEntryData.summary.subAmt)}</td>
              </tr>
              <tr className="text-danger">
                <td className="fw-bold">Consumer Offer</td>
                <td className="text-end">-{formatCurrency(salesEntryData.summary.consumerOffer)}</td>
              </tr>
              <tr className="text-danger">
                <td className="fw-bold">Store Offer</td>
                <td className="text-end">-{formatCurrency(salesEntryData.summary.storeOffer)}</td>
              </tr>
              <tr className="text-danger">
                <td className="fw-bold">Discount Voucher</td>
                <td className="text-end">-{formatCurrency(salesEntryData.summary.discountVoucher)}</td>
              </tr>
              <tr className="text-danger">
                <td className="fw-bold">Total Offers</td>
                <td className="text-end">-{formatCurrency(salesEntryData.summary.totalOffer)}</td>
              </tr>

              {/* Conditionally show GST rows */}
              {salesEntryData?.customer?.isIgst ? (
                <tr>
                  <td>IGST (3%):</td>
                  <td className="text-end">{formatCurrency(salesEntryData.summary.cgst + salesEntryData.summary.sgst)}</td>
                </tr>
              ) : (
                <>
                  <tr>
                    <td>CGST (1.5%):</td>
                    <td className="text-end">{formatCurrency(salesEntryData.summary.cgst)}</td>
                  </tr>
                  <tr>
                    <td>SGST (1.5%):</td>
                    <td className="text-end">{formatCurrency(salesEntryData.summary.sgst)}</td>
                  </tr>
                </>
              )}

              <tr>
                <td className="fw-bold">Total Redeem Amount</td>
                <td className="text-end fw-bold">
                  -{formatCurrency(
                    salesEntryData.items
                      ? Math.round(
                        salesEntryData.items.reduce(
                          (sum: number, item: any) => sum + Number(item.totalRewardDiscount || 0),
                          0
                        ) * 100
                      ) / 100
                      : 0
                  )}
                </td>
              </tr>
              <tr className="table-warning">
                <td className="fw-bold">Total Amount</td>
                <td className="text-end fw-bold">{formatCurrency(salesEntryData.summary.totalAmt)}</td>
              </tr>
              <tr>
                <td className="fw-bold">Total Due Amount</td>
                <td className="text-end fw-bold text-danger">{formatCurrency(Math.max(0, salesEntryData?.summary?.totalDueAmt ?? 0.00))}</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* Payment Details if available */}
        {salesEntryData.payment && salesEntryData.payment.length > 0 && (
          <div className="mb-4">
            <h6 className="mb-3">Payment Details</h6>
            <Table bordered size="sm" className="summary-table">
              <tbody>
                {salesEntryData.payment.map((payment, index) => (
                  <tr key={index}>
                    <td className="fw-bold">{payment.method}</td>
                    <td className="text-end">{formatCurrency(payment.amount)}</td>
                  </tr>
                ))}
                {salesEntryData.voucherDetails.oldGoldAmt > 0 && (
                  <tr className="text-success">
                    <td className="fw-bold">Old Gold Exchange</td>
                    <td className="text-end">{formatCurrency(salesEntryData.voucherDetails.oldGoldAmt)}</td>
                  </tr>
                )}
                <tr className="table-warning">
                  <td className="fw-bold">Total Paid</td>
                  <td className="text-end fw-bold">
                    {formatCurrency(
                      (salesEntryData.payment.reduce((sum, p) => sum + p.amount, 0)) +
                      salesEntryData.voucherDetails.oldGoldAmt
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}

        <style>{`
          .calculation-table th {
            background-color: #f8f9fa !important;
            font-weight: 600;
            font-size: 12px;
            text-align: center;
            vertical-align: middle;
            border: 1px solid #dee2e6;
            white-space: nowrap;
          }
          
          .calculation-table td {
            font-size: 12px;
            vertical-align: middle;
            border: 1px solid #dee2e6;
            padding: 6px 8px;
          }
          
          .calculation-table .text-end {
            text-align: right !important;
          }
          
          .calculation-table tbody tr:hover {
            background-color: #f8f9fa;
          }
          
          .table-warning {
            background-color: #fff3cd !important;
          }
          
          .summary-table td {
            padding: 8px 12px;
          }
          
          .summary-table .text-end {
            text-align: right !important;
            font-family: monospace;
          }
        `}</style>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn add-btn" onClick={onHide}>
          Close
        </button>
        {/* <button type="button" className="btn btn-primary" onClick={() => window.print()}>
          Print Calculation
        </button> */}
      </Modal.Footer>
    </Modal>
  );
};

export default CalculationModal;